// ─── ISO 6346 container numbers ──────────────────────────────────────────────
// A container number is four letters (a three-letter owner code plus a
// one-letter equipment category) followed by six digits and a check digit —
// "CSQU3054383". The check digit is computed from the other eleven characters,
// so a typo in any of them is detectable without asking anyone.
//
// Advisory only: the API stores whatever it is given, because a box in the yard
// occasionally carries a number that fails the check and the operator still has
// to record what is physically on it. So this drives a hint, never a block.

/**
 * ISO 6346 letter values: A = 10, then upwards, skipping every multiple of 11
 * (11, 22, 33) — those are reserved so the modulus can't be fooled.
 */
const LETTER_VALUES: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  let value = 10;
  for (let i = 0; i < 26; i += 1) {
    while (value % 11 === 0) value += 1;
    map[String.fromCharCode(65 + i)] = value;
    value += 1;
  }
  return map;
})();

const PATTERN = /^[A-Z]{4}\d{7}$/;

/**
 * The fourth letter is the equipment category, and the standard allows exactly
 * three: U for a freight container, J for its detachable equipment, Z for a
 * trailer or chassis. Anything else in that position is a misread letter — most
 * often the owner code typed as five characters.
 */
const CATEGORY_LETTERS = ["U", "J", "Z"];

/**
 * A size/type code — "22G1", "45R1", "L5G1".
 *
 * Two characters of size, a type letter and a type digit. It is a different
 * ISO 6346 field that lives on the same door as the number and gets pasted onto
 * the end of it often enough to be worth naming: "MSUU275068022G1" is a valid
 * number with its size/type code stuck to it, not an invalid number, and the
 * container has a box of its own for it.
 */
const SIZE_TYPE = /^[0-9A-Z]{2}[A-Z][0-9]$/;

/** Strip spacing and case, the way the API stores it: "mscu 123456 7" → "MSCU1234567". */
export function normalizeContainerNumber(raw: string): string {
  return raw.replace(/[\s-]/g, "").toUpperCase();
}

/** The check digit the first eleven characters imply, or null if they are malformed. */
export function containerCheckDigit(raw: string): number | null {
  const value = normalizeContainerNumber(raw);
  if (!/^[A-Z]{4}\d{6}/.test(value) || value.length < 10) return null;

  let sum = 0;
  for (let i = 0; i < 10; i += 1) {
    const char = value[i];
    const digit = i < 4 ? LETTER_VALUES[char] : Number(char);
    sum += digit * 2 ** i;
  }
  // The remainder is taken twice: mod 11 gives 0–10, and a remainder of 10 is
  // written as 0 on the box.
  return (sum % 11) % 10;
}

/**
 * Why a number was refused, when it was.
 *
 * Four separate answers rather than one "invalid", because they call for four
 * different actions: retype the shape, fix one letter, re-read the digits off
 * the box, or move the tail into the ISO code field.
 */
export type ContainerNumberProblem =
  | "format"
  | "category"
  | "checkDigit"
  | "sizeType";

/**
 * How a typed container number reads to us.
 *
 * "incomplete" covers both empty and half-typed, so a field being filled in
 * doesn't flash an error at every keystroke. `expected` is the check digit the
 * first ten characters imply — the one useful thing to say to somebody whose
 * eleventh character disagrees.
 */
export interface ContainerNumberCheck {
  state: "incomplete" | "valid" | "invalid";
  problem?: ContainerNumberProblem;
  expected?: number;
  /** The trailing size/type code, when that is what the tail turned out to be. */
  sizeTypeCode?: string;
}

export function checkContainerNumber(raw: string): ContainerNumberCheck {
  const value = normalizeContainerNumber(raw);
  if (!value) return { state: "incomplete" };

  // Checked before the length verdict below: this one is longer than a number,
  // and calling it "too long" would be true and useless.
  if (value.length === 15) {
    const head = value.slice(0, 11);
    const tail = value.slice(11);
    if (PATTERN.test(head) && SIZE_TYPE.test(tail)) {
      return { state: "invalid", problem: "sizeType", sizeTypeCode: tail };
    }
  }

  if (!PATTERN.test(value)) {
    return value.length < 11
      ? { state: "incomplete" }
      : { state: "invalid", problem: "format" };
  }

  if (!CATEGORY_LETTERS.includes(value[3])) {
    return { state: "invalid", problem: "category" };
  }

  const expected = containerCheckDigit(value);
  if (expected === null || expected !== Number(value[10])) {
    return {
      state: "invalid",
      problem: "checkDigit",
      ...(expected === null ? {} : { expected }),
    };
  }

  return { state: "valid" };
}

/**
 * The message a refusal deserves, as an i18n key and its values.
 *
 * Lives here rather than in either page because both of them — the
 * dispatcher's form and the driver's own screen — have to say the same thing
 * about the same number, and a second copy of this mapping is how they stop
 * doing that. Keys are in the `containerOrders` namespace, which is the one
 * both pages load.
 */
export function containerNumberMessage(
  check: ContainerNumberCheck,
): { key: string; values?: Record<string, unknown> } | null {
  if (check.state !== "invalid") return null;
  switch (check.problem) {
    case "sizeType":
      return {
        key: "lineEdit.hints.sizeTypeSuffix",
        values: { code: check.sizeTypeCode },
      };
    case "category":
      return { key: "lineEdit.hints.categoryLetter" };
    case "checkDigit":
      // `expected` is always there for this problem — the shape has already
      // been checked — but the plain wording is kept as the honest fallback
      // rather than printing "undefined" at somebody.
      return check.expected === undefined
        ? { key: "lineEdit.hints.containerNumberInvalid" }
        : {
            key: "lineEdit.hints.checkDigitInvalid",
            values: { expected: check.expected },
          };
    default:
      // The shape is wrong. Said as a refusal rather than reusing the neutral
      // "four letters and seven digits" hint, which is the same sentence the
      // field carries while it is being typed into and reads as advice, not as
      // the reason a save just stopped.
      return { key: "lineEdit.hints.containerNumberInvalid" };
  }
}

/** Grouped for reading — "CSQU3054383" → "CSQU 305438 3". */
export function formatContainerNumber(raw: string): string {
  const value = normalizeContainerNumber(raw);
  if (!PATTERN.test(value)) return value;
  return `${value.slice(0, 4)} ${value.slice(4, 10)} ${value.slice(10)}`;
}
